import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSwipeable } from "react-swipeable";
import "./../../../../src/index.scss";
import "./SongFullScreen.scss";
import draftToHtml from "draftjs-to-html";
import RestClient from "../../../@core/restClient";

import {
  IoChevronDownOutline,
  IoChevronUpOutline,
  IoCloseOutline,
  IoMusicalNotesSharp,
  IoChatbubbleEllipsesOutline,
  IoMenuOutline
} from "react-icons/io5";
import { FaRegFilePdf } from "react-icons/fa";
import { TbColumns2 } from "react-icons/tb";
import { Auth } from "../../../@core/auth";
import KeyList from "./KeyList";
import Spinner from "../../widgets/UI/Spinner";

const flatKey = [
  "C",
  "G",
  "D",
  "A",
  "E",
  "B",
  "Gb",
  "Db",
  "Ab",
  "Eb",
  "Bb",
  "F"
];
const sharpKey = [
  "C",
  "G",
  "D",
  "A",
  "E",
  "B",
  "F#",
  "C#",
  "G#",
  "D#",
  "A#",
  "F"
];
const chordCheck = [
  "Ab",
  "Eb",
  "Bb",
  "Gb",
  "Db",
  "F#",
  "C#",
  "G#",
  "D#",
  "A#",
  "F",
  "C",
  "G",
  "D",
  "A",
  "E",
  "B"
];

const parseSongKeys = (value: any): { songId: number; key: string }[] | null => {
  if (!value) return null;
  if (Array.isArray(value)) return value;
  try { return JSON.parse(value); } catch { return null; }
};

const SongFullScreen = ({
  selectedSongs: propSongs,
  closeFullScreen,
  scroll,
  selectionId,
  songKeys: propSongKeys,
  onKeysUpdate
}: any) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialSongs = propSongs || [];
  const [songs, setSongs] = useState<any[]>(initialSongs);
  const handleClose =
    closeFullScreen ||
    (() => navigate(localStorage.getItem("lastVisitedUrl") || "/"));

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const handleSaveAndClose = () => {
    if (propSongs && selectionId) {
      const baseline = sessionBaselineRef.current.length ? sessionBaselineRef.current : songs.map((s: any) => s.key);
      const keysChanged = songs.some((_: any, i: number) => originalKey[i] !== baseline[i]);
      if (keysChanged) {
        setShowSaveKeysModal(true);
        return;
      }
    }
    handleClose();
  };

  const confirmSaveKeys = async (save: boolean) => {
    setShowSaveKeysModal(false);
    if (save) {
      const newSongKeys = songs.map((song: any, i: number) => ({ songId: song.id, key: originalKey[i] }));
      try {
        await RestClient.editSelection(selectionId, { songKeys: JSON.stringify(newSongKeys) });
        onKeysUpdate?.(selectionId, newSongKeys);
      } catch {
        setSaveKeysError(true);
        return;
      }
    }
    handleClose();
  };

  const [songSettings, setSongSettings] = useState<any>(
    Array(initialSongs.length + 1).fill({
      noChords: false,
      noComments: true,
      layout: false,
      fontSize: 1.8
    })
  );

  const [displayButtons, setDisplayButtons] = useState(true);
  const [keyList, setKeyList] = useState(false);
  const [newKey, setNewKey] = useState(Array(initialSongs.length + 1).fill(""));
  const [originalKey, setOriginalKey] = useState(
    initialSongs.map((song: any) => song.key)
  );
  const [songIndex, setSongIndex] = useState(100);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saveKeysError, setSaveKeysError] = useState(false);
  const [showSaveKeysModal, setShowSaveKeysModal] = useState(false);
  const [pendingKeys, setPendingKeys] = useState<{ songId: number; key: string }[] | null>(() =>
    parseSongKeys(propSongKeys)
  );
  const sessionBaselineRef = useRef<string[]>([]);
  const [screen, setScreen] = useState("narrowScreen");
  const [bigBtnHeight, setBigBtnHeight] = useState(0);
  const screenWidth = window.innerWidth;
  const carousel = useRef() as React.MutableRefObject<HTMLDivElement>;

  //LISTEN TO SCREEN ORIENTATION

  useEffect(() => {
    const updateScreen = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setScreen(w >= 768 && w > h ? "wideScreen" : "narrowScreen");
    };

    updateScreen();
    window.addEventListener("resize", updateScreen);
    window.addEventListener("orientationchange", updateScreen);

    return () => {
      window.removeEventListener("resize", updateScreen);
      window.removeEventListener("orientationchange", updateScreen);
    };
  }, []);

  const togleKeyList = (e: any, index: any) => {
    e.stopPropagation();
    setSongIndex(index);
    setKeyList(!keyList);
  };

  useEffect(() => {
    if (keyList) {
      document.body.addEventListener("click", () => {
        setKeyList(false);
      });
    } else {
      return;
    }
  }, [keyList]);

  const printPdf = async () => {
    const html2pdf = await require("html2pdf.js");

    const opt = {
      margin: 0.1,
      filename: "mysongs.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 3 },
      jsPDF: { unit: "in", format: "A4", orientation: "portrait" }
    };

    html2pdf()
      .set(opt)
      .from(carousel.current.innerHTML)
      .outputPdf("blob") // Get PDF as blob
      .then((pdfBlob: Blob) => {
        const pdfURL = URL.createObjectURL(pdfBlob);
        window.open(pdfURL, "_blank");
      });
  };

  //CHANGING KEY

  const transposeAtIndex = (index: number, fromKey: string, toKey: string) => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    const splitFrom = fromKey.replace("m", "");
    const splitTo = toKey.replace("m", "");
    const startKey = splitFrom.includes("#")
      ? sharpKey.indexOf(splitFrom) + 1
      : flatKey.indexOf(splitFrom) + 1;
    const endKey = splitTo.includes("#")
      ? sharpKey.indexOf(splitTo) + 1
      : flatKey.indexOf(splitTo) + 1;
    const shift =
      startKey > endKey
        ? flatKey.length - startKey + endKey
        : endKey - startKey;
    if (shift === 0) return;
    const checkKey = new RegExp("(D|E|G|A|B|C#|F#|G#|A#)(?![#b])", "g");
    const allSongs = Array.from(carousel.current.children);
    if (!allSongs[index]) return;
    const oneSong = allSongs[index].querySelectorAll(".song-body p");
    Array.from(oneSong).forEach((item: any) => {
      const checked = item.innerHTML.match(check);
      const excluded = item.innerHTML.match(exclude);
      if (checked && excluded === null) {
        chordCheck.forEach((chord) => {
          const checkChord = new RegExp(`\\b${chord}(?![#b])`, "g");
          if (item.innerHTML.match(checkChord)) {
            const oldI = chord.includes("b")
              ? flatKey.indexOf(chord)
              : sharpKey.indexOf(chord);
            const newChord = toKey.match(checkKey)
              ? `-${sharpKey[(oldI + shift) % 12]}`
              : `-${flatKey[(oldI + shift) % 12]}`;
            const re = new RegExp(`(?<!-)${chord}`, "g");
            item.innerHTML = item.innerHTML.replaceAll(re, newChord);
          }
        });
        item.innerHTML = item.innerHTML.replaceAll("-", "");
      }
    });
  };

  const changeKey = (passedKey: any) => {
    setKeyList(false);
    const changedKey = newKey.map((item: any, i: any) => {
      if (i === songIndex) {
        return (item = passedKey);
      } else {
        return item;
      }
    });
    setNewKey(changedKey);
  };
  // MANIPULATING CHORDS
  const check = /([A-G](#|b)?(m|maj|min|dim|aug|sus|add)?\d*(\/[A-G](#|b)?)?)+/;
  const exclude =
    /c|e|f|h|k|l|o|q|r|t|v|w|x|y|z|VERSE|CHORUS|TAG|VAMP|INTRO|BRIDGE|PRE|INTERLUDE/;

  const extraLines = /[*]/;
  const verseLine = /[%]/;
  const chorusLine = /[$]/;
  const bridgeLine = /[@]/;
  useEffect(() => {
    if (songIndex !== 100) {
      transposeAtIndex(songIndex, originalKey[songIndex], newKey[songIndex]);
    }
    const changedKey = originalKey.map((item: any, i: any) =>
      i === songIndex ? newKey[songIndex] : item
    );
    setOriginalKey(changedKey);
  }, [newKey]);

  //MAKING ALL CHORDS BLUE
  useEffect(() => {
    Array.from(carousel.current.children).forEach((el) => {
      const card = el.querySelectorAll(".song-body p");
      Array.from(card).forEach((par) => {
        const checked = par.innerHTML.match(check);
        const excluded = par.innerHTML.match(exclude);
        const comments = par.innerHTML.match(extraLines);
        const verse = par.innerHTML.match(verseLine);
        const chorus = par.innerHTML.match(chorusLine);
        const bridge = par.innerHTML.match(bridgeLine);
        if (checked && excluded === null) {
          par.classList.add("chords");
        } else {
          par.classList.remove("chords");
        }
        if (comments) {
          par.classList.add("comments");
          par.classList.add("hideScreenMode");
        } else {
          par.classList.remove("comments");
          par.classList.remove("hideScreenMode");
        }
        if (verse) {
          par.classList.add("verse");
          const final = par.innerHTML.replaceAll("%", "");
          par.innerHTML = final;
        } else if (chorus) {
          par.classList.add("chorus");
          const final = par.innerHTML.replaceAll("$", "");
          par.innerHTML = final;
        } else if (bridge) {
          par.classList.add("bridge");
          const final = par.innerHTML.replaceAll("@", "");
          par.innerHTML = final;
        } else {
          return;
        }
      });
    });
    if (!propSongs) applySettings(songSettings);
  }, [songs]);

  useEffect(() => {
    if (!songs.length || !carousel.current) return;
    if (pendingKeys) {
      const updatedKeys = songs.map((s: any) => s.key);
      pendingKeys.forEach(({ songId, key }) => {
        const i = songs.findIndex((s: any) => s.id === songId);
        if (i === -1 || !key || key === songs[i].key) return;
        transposeAtIndex(i, songs[i].key, key);
        updatedKeys[i] = key;
      });
      setOriginalKey(updatedKeys);
      sessionBaselineRef.current = [...updatedKeys];
      setPendingKeys(null);
    } else if (!sessionBaselineRef.current.length) {
      sessionBaselineRef.current = songs.map((s: any) => s.key);
    }
  }, [pendingKeys, songs]);

  //NIDING CHORDS AND COMMENTS
  useEffect(() => {
    if (songIndex !== 100) {
      const allSongs = Array.from(carousel.current.children);
      const oneSong = allSongs[songIndex].querySelectorAll(".song-body p");

      Array.from(oneSong).forEach((par) => {
        const checked = par.innerHTML.match(check);
        const excluded = par.innerHTML.match(exclude);
        const comments = par.innerHTML.match(extraLines);

        if (songSettings[songIndex].noChords && checked && excluded === null) {
          par.classList.add("hideScreenMode");
          return;
        } else {
          par.classList.remove("hideScreenMode");
        }
        if (songSettings[songIndex].noComments && comments) {
          par.classList.add("hideScreenMode");
        } else {
          par.classList.remove("hideScreenMode");
        }
      });
    }
  }, [songSettings]);

  const applySettings = (settings: any) => {
    //const allSongs = Array.from(carousel.current.children);

    Array.from(carousel.current.children).forEach((song, i) => {
      const oneSong = song.querySelectorAll(".song-body p");

      Array.from(oneSong).forEach((par) => {
        const checked = par.innerHTML.match(check);
        const excluded = par.innerHTML.match(exclude);
        const comments = par.innerHTML.match(extraLines);

        if (settings[i].noChords && checked && excluded === null) {
          par.classList.add("hideScreenMode");
          return;
        } else {
          par.classList.remove("hideScreenMode");
        }
        if (settings[i].noComments && comments) {
          par.classList.add("hideScreenMode");
        } else {
          par.classList.remove("hideScreenMode");
        }
      });
    });
  };

  const toggleChords = (index: any) => {
    //e.stopPropagation();
    setSongIndex(index);

    const newSettingsArray = songSettings.map((set: any, i: any) => {
      if (i === index) {
        return { ...set, noChords: !set.noChords };
      } else {
        return set;
      }
    });
    setSongSettings(newSettingsArray);
  };

  const toggleComments = (index: any) => {
    //e.stopPropagation();
    setSongIndex(index);
    const newSettingsArray = songSettings.map((set: any, i: any) => {
      if (i === index) {
        return { ...set, noComments: !set.noComments };
      } else {
        return set;
      }
    });
    setSongSettings(newSettingsArray);
  };

  // CHANGE LAYOUT OR FONTSIZE
  const toggleLayout = (index: any) => {
    //e.stopPropagation();
    setSongIndex(index);
    const newSettingsArray = songSettings.map((set: any, i: any) => {
      if (i === index) {
        return { ...set, layout: !set.layout };
      } else {
        return set;
      }
    });
    setSongSettings(newSettingsArray);
  };

  //CHANGING FONT SIZE

  const changeFontUp = (index: any) => {
    setSongIndex(index);
    const newSettingsArray = songSettings.map((set: any, i: any) => {
      if (i === index) {
        return { ...set, fontSize: set.fontSize + 0.1 };
      } else {
        return set;
      }
    });
    setSongSettings(newSettingsArray);
  };
  const changeFontDown = (index: any) => {
    setSongIndex(index);
    const newSettingsArray = songSettings.map((set: any, i: any) => {
      if (i === index) {
        return (set = { ...set, fontSize: set.fontSize - 0.1 });
      } else {
        return set;
      }
    });
    setSongSettings(newSettingsArray);
  };

  useEffect(() => {
    if (propSongs) return;
    const selectionId = searchParams.get("selectionId");
    if (!selectionId) return;
    setIsLoading(true);
    RestClient.getSelection(Number(selectionId)).then((selection: any) => {
      const ordered: any[] = [];
      selection.sort.forEach((songNumber: any) => {
        const song = selection.songs.find((s: any) => s.id === songNumber);
        if (song) ordered.push(song);
      });
      setSongs(ordered);
      setSongSettings(
        Array(ordered.length + 1).fill({
          noChords: true,
          noComments: true,
          layout: false,
          fontSize: 1.8
        })
      );
      setNewKey(Array(ordered.length + 1).fill(""));
      setOriginalKey(ordered.map((s: any) => s.key));
      const parsed = parseSongKeys(selection.songKeys);
      if (parsed) setPendingKeys(parsed);
      setIsLoading(false);
    }).catch(() => {
      navigate("/");
    });
  }, []);

  //DISPLAYING SCROLL BUTTONS

  useEffect(() => {
    if (!songs.length) return;
    const screen = window.innerWidth;
    if (songs.length !== 1) {
      if (screen <= 992 && !(scroll ?? true)) {
        setDisplayButtons(false);
      } else {
        setDisplayButtons(true);
      }
    } else {
      setDisplayButtons(false);
    }
  }, [songs.length]);

  const carouselIncrement = (delta: any) => {
    if (carousel.current) {
      const width = carousel.current.offsetWidth;
      carousel.current.scrollTo(carousel.current.scrollLeft + width * delta, 0);
    }
  };

  useEffect(() => {
    const el = carousel.current;
    if (!el || !songs.length) return;
    let timer: ReturnType<typeof setTimeout>;
    const onScroll = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const index = Math.round(el.scrollLeft / el.offsetWidth);
        setSongIndex(index);
        const card = el.children[index];
        if (card) {
          const body = (card.querySelector(".song-body-text") || card.querySelector(".song-body")) as HTMLElement | null;
          if (body) body.scrollTop = 0;
        }
      }, 50);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => { el.removeEventListener("scroll", onScroll); clearTimeout(timer); };
  }, [songs]);

  useEffect(() => {
    if (!songs.length) return;
    const measure = () => {
      requestAnimationFrame(() => {
        const songBody = carousel.current?.children[0]?.querySelector(".song-body") as HTMLElement | null;
        if (songBody) setBigBtnHeight(songBody.clientHeight);
      });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [songs]);

  const setCarouselRef = (el: HTMLDivElement) => {
    carousel.current = el;
  };

  const { ref: _swipeRef, ...swipeEvents } = useSwipeable({
    onSwipedLeft: () => carouselIncrement(1),
    onSwipedRight: () => carouselIncrement(-1),
    preventScrollOnSwipe: false,
    trackMouse: false,
    touchEventOptions: { passive: true }
  });
  useEffect(() => {
    if (!menuOpen) return;

    const handleBodyClick = () => {
      setMenuOpen(false);
    };

    document.body.addEventListener("click", handleBodyClick);

    // Cleanup when menu closes or component unmounts
    return () => {
      document.body.removeEventListener("click", handleBodyClick);
    };
  }, [menuOpen]);

  // SAVING SONG SETTINGS

  const saveOptions = async (id: any, i: any) => {
    const settings = {
      songId: id,
      setting: JSON.stringify({ ...songSettings[i], key: originalKey[i] })
    };

    try {
      setIsLoading(true);
      await RestClient.saveSongSetting(settings);
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
    setMenuOpen(false);
  };

  useEffect(
    function () {
      if (!songs.length || !propSongs || !Auth.isAuthenticated) return;
      const songIds = songs.map((song: any) => song.id);

      let updatedSongSettings = [...songSettings];
      (async () => {
        try {
          setIsLoading(true);
          const response = await RestClient.getSongSetting(songIds);

          songs.forEach((song: any, index: number) => {
            const settingRecord = response.find(
              (item: any) => item.songId === song.id
            );

            if (settingRecord?.setting) {
              let parsedSetting = {};
              try {
                parsedSetting = JSON.parse(settingRecord.setting);
              } catch (parseError) {
                console.warn(
                  `Failed to parse setting for song ${song.id}`,
                  parseError
                );
                return; // skip this song if JSON is corrupted
              }

              // Only update if we have valid parsed setting
              if (parsedSetting !== undefined) {
                updatedSongSettings = updatedSongSettings.map(
                  (set: any, i: any) => {
                    if (i === index) {
                      return (set = parsedSetting);
                    } else {
                      return set;
                    }
                  }
                );
              } else {
                return;
              }
            }

            // If no setting exists → do nothing (keep current values or defaults)
          });
        } catch (error) {
          console.log(error);
        }
        setSongSettings(updatedSongSettings);
        applySettings(updatedSongSettings);

        if (!propSongKeys) {
          const updatedOriginalKey = [...originalKey];
          const updatedNewKey = [...newKey];
          let keysChanged = false;
          songs.forEach((song: any, index: number) => {
            const savedKey = (updatedSongSettings[index] as any).key;
            if (savedKey && savedKey !== song.key) {
              transposeAtIndex(index, song.key, savedKey);
              updatedOriginalKey[index] = savedKey;
              updatedNewKey[index] = savedKey;
              keysChanged = true;
            }
          });
          if (keysChanged) {
            setOriginalKey(updatedOriginalKey);
            setNewKey(updatedNewKey);
          }
        }

        setIsLoading(false);
      })();
    },
    [songs]
  );

  const currentSong = songs[songIndex < songs.length ? songIndex : 0];

  return (
    <div className="full-screen">
      {currentSong && (
        <Helmet>
          <title>{currentSong.title} | ARYN Lyrics</title>
        </Helmet>
      )}
      {showSaveKeysModal && (
        <div className="full-screen-error-modal">
          <div className="full-screen-error-modal-box">
            <p className="full-screen-error-modal-text">
              Зберегти нові тональності для цієї добірки?
            </p>
            <div className="full-screen-error-modal-btns">
              <button className="btn-mcolor" style={{ width: "10rem" }} onClick={() => confirmSaveKeys(true)}>
                Так
              </button>
              <button className="btn-plain" style={{ width: "10rem" }} onClick={() => confirmSaveKeys(false)}>
                Ні
              </button>
            </div>
          </div>
        </div>
      )}
      {saveKeysError && (
        <div className="full-screen-error-modal">
          <div className="full-screen-error-modal-box">
            <p className="full-screen-error-modal-text">
              Змінені тональності не вдалося зберегти.
            </p>
            <div className="full-screen-error-modal-btns">
              <button className="btn-mcolor" onClick={() => setSaveKeysError(false)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {isLoading && !songs.length && <Spinner />}
      <div ref={setCarouselRef} className="full-screen-inner" {...swipeEvents}>
        {songs.map((item: any, i: any) => (
          <div key={item.id} className="card">
            {isLoading ? <Spinner /> : ""}
            <div className="title-line">
              <div className="title-line-head">
                <p className="title-line-title">{item.title}</p>
                <div className="my-tooltip title-icon">
                  <IoMenuOutline
                    //className="title-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpen(!menuOpen);
                    }}
                  />
                  <span className="my-tooltip-text tooltips-songbook">
                    Меню
                  </span>
                </div>
              </div>
              {menuOpen ? (
                <div
                  className="menu"
                  onClick={(e) => e.stopPropagation()}
                  data-html2canvas-ignore
                >
                  <div className="title-line-inner">
                    <div className="key">
                      <div onClick={(e) => togleKeyList(e, i)}>
                        <input
                          type="text"
                          className="title-line-key"
                          value={originalKey[i]}
                          onChange={() => console.log("")}
                        />
                        {keyList ? (
                          <IoChevronUpOutline className="key-icon" />
                        ) : (
                          <IoChevronDownOutline className="key-icon" />
                        )}
                      </div>
                      {keyList ? (
                        <div className="key-box">
                          <KeyList changeKey={changeKey} originalKey={songs[songIndex]?.key} />
                        </div>
                      ) : (
                        ""
                      )}
                    </div>
                    {/* <p className="title-line-key">{item.key}</p> */}
                    {item.tempo ? (
                      <p className="title-line-tempo">
                        tempo {item.tempo}, {item.time}
                      </p>
                    ) : (
                      ""
                    )}
                    <div className="buttons">
                      <button
                        className={
                          songSettings[i].noChords
                            ? "chords-show btnOff"
                            : "chords-show"
                        }
                        onClick={() => toggleChords(i)}
                      >
                        <IoMusicalNotesSharp />
                      </button>
                      <button
                        className={
                          songSettings[i].noComments
                            ? "comments-show btnOff"
                            : "comments-show"
                        }
                        onClick={() => toggleComments(i)}
                      >
                        <IoChatbubbleEllipsesOutline />
                      </button>
                      {screenWidth > 600 ? (
                        <button
                          className={
                            songSettings[i].layout
                              ? "comments-show"
                              : "comments-show btnOff"
                          }
                          onClick={() => toggleLayout(i)}
                        >
                          <TbColumns2 />
                        </button>
                      ) : null}
                      <button className="printBtn" onClick={printPdf}>
                        <FaRegFilePdf />
                      </button>
                    </div>
                  </div>
                  <div className="change-font-size font-position">
                    Aa
                    <div className="change-font-size-arrows">
                      <IoChevronUpOutline
                        className="icon"
                        onClick={() => changeFontUp(i)}
                      />
                      <IoChevronDownOutline
                        className="icon"
                        onClick={() => changeFontDown(i)}
                      />
                    </div>
                  </div>
                  {Auth.isAuthenticated && !!propSongs ? (
                    <div className="my-tooltip">
                      <button
                        className="btn-mcolor"
                        onClick={() => saveOptions(item.id, i)}
                      >
                        Save
                      </button>
                      <span className="my-tooltip-text tooltips-songbook">
                        Зберегти налаштування для цієї пісні
                      </span>
                    </div>
                  ) : null}
                </div>
              ) : (
                ""
              )}
              <IoCloseOutline onClick={handleSaveAndClose} className="close-icon" />
            </div>
            <div className="song-body">
              {displayButtons && i === (songIndex < songs.length ? songIndex : 0) && (
                <div className="bigBtn-anchor">
                  <div className="bigBtn bigLeft" style={{ height: bigBtnHeight }} onClick={() => carouselIncrement(-1)} data-html2canvas-ignore>
                    <div className="bigBtn-carousel-btn btn-left"></div>
                  </div>
                  <div className="bigBtn bigRight" style={{ height: bigBtnHeight }} onClick={() => carouselIncrement(1)} data-html2canvas-ignore>
                    <div className="bigBtn-carousel-btn btn-right"></div>
                  </div>
                </div>
              )}
              <div
                dangerouslySetInnerHTML={{
                  __html: draftToHtml(JSON.parse(item.text))
                }}
                style={{ fontSize: `${songSettings[i].fontSize}rem` }}
                className={
                  songSettings[i].layout && screenWidth > 600
                    ? `song-body-text ${screen}`
                    : "song-body-one-column"
                }
              ></div>
              {!propSongs && (
                <a href="https://aryn-lyrics.com" className="full-screen-brand">
                  Aryn-lyrics.com
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
export default SongFullScreen;
